import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'

import { useUpdateBusinessCard } from '@/api/api-mutations'
import { preloadGetCompany, useGetCompany, useListBusinessCards } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { Prompt, usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Plus } from '@/icons/plus'
import { XMarkMini } from '@/icons/x-mark-mini'
import { useDebounce } from '@/utils/hooks'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/_general/companies/$companyUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit', '/settlements']}>
			<CompanyDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { companyUuid } }) =>
		await preloadGetCompany(context, {
			path: { companyUuid }
		}),
	staticData: {
		crumb: ({ loaderData }) => loaderData?.name
	}
})

function CompanyDetail() {
	const { L } = useI18n()
	const { companyUuid } = Route.useParams()
	const { data: company } = useGetCompany({ companyUuid })
	const prompt = usePrompt()
	const updateCard = useUpdateBusinessCard(false, { companyUuid })

	const [assignDialogOpen, setAssignDialogOpen] = useState(false)
	const [cardSearch, setCardSearch] = useState('')
	const debouncedSearch = useDebounce(cardSearch)

	const searchResults = useListBusinessCards(
		{ limit: 10, offset: 0, q: debouncedSearch || undefined },
		{ enabled: assignDialogOpen && debouncedSearch.length > 0 }
	)

	if (!company) {
		return <LoadingSpinner centered />
	}

	const handleAssignCard = async (cardUuid: string) => {
		try {
			const result = await updateCard({
				path: { cardUuid },
				body: { companyUuid }
			})
			if (result.isSuccess) {
				toast.success(L.Companies.Detail.AssignCardSuccess())
				setAssignDialogOpen(false)
				setCardSearch('')
			}
		} catch (e: any) {
			toast.error(e.message)
		}
	}

	const handleUnassignCard = async (cardUuid: string) => {
		const res = await prompt({
			title: L.Companies.Detail.UnassignCard(),
			description: L.Companies.Detail.UnassignCardConfirm(),
			confirmText: L.General.Confirm(),
			cancelText: L.General.Cancel()
		})
		if (!res) return

		try {
			await updateCard({
				path: { cardUuid },
				body: { companyUuid: null }
			})
			toast.success(L.Companies.Detail.UnassignCardSuccess())
		} catch (e: any) {
			toast.error(e.message)
		}
	}

	const handleCloseAssignDialog = () => {
		setAssignDialogOpen(false)
		setCardSearch('')
	}

	return (
		<>
			<SingleColumnPage hasOutlet>
				<Container className='divide-y'>
					<ContainerHeader
						heading={company.name}
						rightSide={
							<Link from={Route.fullPath} to='edit'>
								<IconButton tooltipText={L.General.Edit()}>
									<PencilSquare />
								</IconButton>
							</Link>
						}
					/>
					<SectionRow title={L.Fields.Name()} value={company.name || '-'} />
					<SectionRow title={L.Fields.ExternalId()} value={company.externalId ?? '-'} />
					<SectionRow
						title={L.Fields.CreatedAt()}
						value={company.createdAt ? new Date(company.createdAt).toLocaleDateString() : '-'}
					/>
					<SectionRow
						title={L.Fields.UpdatedAt()}
						value={company.updatedAt ? new Date(company.updatedAt).toLocaleDateString() : '-'}
					/>
				</Container>

				<Container className='divide-y'>
					<ContainerHeader heading={L.Companies.Detail.ContactInfo()} />
					<SectionRow title={L.Fields.Email()} value={company.email ?? '-'} />
					<SectionRow title={L.Fields.Phone()} value={company.phone ?? '-'} />
					<SectionRow title={L.Fields.OrgNumber()} value={company.orgNumber ?? '-'} />
				</Container>

				<Container className='divide-y'>
					<ContainerHeader
						heading={L.BusinessCards.Title()}
						rightSide={
							<IconButton
								tooltipText={L.Companies.Detail.AssignCard()}
								onClick={() => setAssignDialogOpen(true)}
							>
								<Plus />
							</IconButton>
						}
					/>
					{company.cards && company.cards.length > 0 ? (
						company.cards.map(card => (
							<div key={card.uuid} className='flex items-center justify-between px-6 py-3'>
								<Link
									to='/business-cards/$cardUuid'
									params={{ cardUuid: card.uuid }}
									className='flex flex-1 flex-col'
								>
									<Text size='small' weight='plus'>
										{card.name}
									</Text>
									<Text size='xsmall' className='text-ui-fg-muted'>
										{card.cardNumber}
									</Text>
								</Link>
								<IconButton
									size='xsmall'
									variant='transparent'
									tooltipText={L.Companies.Detail.UnassignCard()}
									onClick={() => handleUnassignCard(card.uuid)}
								>
									<XMarkMini />
								</IconButton>
							</div>
						))
					) : (
						<div className='px-6 py-4'>
							<Text size='small' leading='compact' className='text-ui-fg-muted'>
								{L.General.NoResultsTitle()}
							</Text>
						</div>
					)}
				</Container>

				{company.recentSettlements && company.recentSettlements.length > 0 && (
					<Container>
						<ContainerHeader heading={L.Companies.RecentSettlements()} />
						<div className='space-y-2 p-6'>
							{company.recentSettlements.map(settlement => (
								<Link
									key={settlement.uuid}
									to='/settlements/$settlementUuid'
									params={{ settlementUuid: settlement.uuid }}
									className='flex items-center justify-between rounded-md border p-3 hover:bg-ui-bg-subtle-hover'
								>
									<div>
										<Text size='small' weight='plus'>
											{L.Date.Short(settlement.periodStart)} -{' '}
											{L.Date.Short(settlement.periodEnd)}
										</Text>
										{settlement.settledAt && (
											<Text size='xsmall' className='text-ui-fg-muted'>
												Settled: {L.Date.Short(settlement.settledAt)}
											</Text>
										)}
									</div>
									<Text size='small'>
										{L.Amount.Default({
											amount: settlement.totalAmount,
											currencyCode: 'NOK'
										})}
									</Text>
								</Link>
							))}
						</div>
					</Container>
				)}
			</SingleColumnPage>

			<Prompt open={assignDialogOpen} variant='confirmation'>
				<Prompt.Content>
					<Prompt.Header>
						<Prompt.Title>{L.Companies.Detail.AssignCard()}</Prompt.Title>
						<Prompt.Description>{L.Companies.Detail.AssignCardDescription()}</Prompt.Description>
					</Prompt.Header>
					<div className='flex flex-col gap-y-3 border-y border-ui-border-base px-6 py-4'>
						<Input
							autoFocus
							size='small'
							type='search'
							placeholder={L.Companies.Detail.CardNumberPlaceholder()}
							value={cardSearch}
							onChange={e => setCardSearch(e.target.value)}
						/>
						{debouncedSearch.length > 0 && (
							<div className='flex max-h-[200px] flex-col gap-y-1 overflow-y-auto'>
								{searchResults.isLoading ? (
									<Text size='small' leading='compact' className='py-2 text-center text-ui-fg-muted'>
										...
									</Text>
								) : searchResults.data?.cards && searchResults.data.cards.length > 0 ? (
									searchResults.data.cards.map(card => (
										<button
											key={card.uuid}
											type='button'
											className='flex items-center justify-between rounded-md px-3 py-2 text-left transition-colors hover:bg-ui-bg-base-hover'
											onClick={() => handleAssignCard(card.uuid)}
										>
											<div className='flex flex-col'>
												<Text size='small' weight='plus' leading='compact'>
													{card.name}
												</Text>
												<Text size='xsmall' leading='compact' className='text-ui-fg-muted'>
													{card.cardNumber}
												</Text>
											</div>
										</button>
									))
								) : (
									<Text size='small' leading='compact' className='py-2 text-center text-ui-fg-muted'>
										{L.General.NoResultsTitle()}
									</Text>
								)}
							</div>
						)}
					</div>
					<Prompt.Footer>
						<Prompt.Cancel onClick={handleCloseAssignDialog}>{L.General.Cancel()}</Prompt.Cancel>
					</Prompt.Footer>
				</Prompt.Content>
			</Prompt>
		</>
	)
}
